import { useState, useEffect, useCallback, useMemo } from "react";
import { C, S, BASE } from "../constants";
import Img from "../components/Img";

const TODAY = new Date().toISOString().slice(0, 10);
const ONE_YEAR = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
const SOON_THRESHOLD = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

function StatusBadge({ status }) {
  const map = {
    active:  { label: "Активний", color: C.green },
    locked:  { label: "Заблокований", color: C.yellow },
    blocked: { label: "Заблокований", color: C.pink },
  };
  const s = map[status] || { label: status, color: C.text4 };
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 6,
      background: s.color + "22", color: s.color, border: `1px solid ${s.color}40`,
    }}>{s.label}</span>
  );
}

function ProductPicker({ value, allProducts, onChange, placeholder = "Пошук товару..." }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  const filtered = q.length >= 2
    ? (allProducts || []).filter(p =>
        p.name?.toLowerCase().includes(q.toLowerCase()) ||
        (p.article || "").toLowerCase().includes(q.toLowerCase()) ||
        String(p.product_id).includes(q)
      ).slice(0, 8)
    : [];

  const selected = value
    ? (allProducts || []).find(p => p.product_id === value)
    : null;

  return (
    <div style={{ position: "relative" }}>
      {selected ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8,
          background: C.bg1, border: `1px solid ${C.border}`, borderRadius: 8, padding: "6px 10px" }}>
          <Img src={selected.image_url} size={28} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: C.text1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selected.name}</div>
            <div style={{ fontSize: 10, color: C.text4 }}>{selected.article}</div>
          </div>
          <span onClick={() => onChange(null)} style={{ cursor: "pointer", color: C.text4, fontSize: 16, lineHeight: 1 }}>×</span>
        </div>
      ) : (
        <div style={{ position: "relative" }}>
          <input
            value={q}
            onChange={e => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 200)}
            placeholder={placeholder}
            style={{ ...S.input, width: "100%", boxSizing: "border-box" }}
          />
          {open && filtered.length > 0 && (
            <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 50,
              background: C.bg0, border: `1px solid ${C.border}`, borderRadius: 8,
              boxShadow: "0 4px 16px rgba(0,0,0,.3)", maxHeight: 240, overflowY: "auto" }}>
              {filtered.map(p => (
                <div key={p.product_id}
                  onMouseDown={() => { onChange(p.product_id); setQ(""); setOpen(false); }}
                  style={{ display: "flex", gap: 8, alignItems: "center", padding: "8px 12px", cursor: "pointer",
                    borderBottom: `1px solid ${C.border}` }}
                  onMouseEnter={e => e.currentTarget.style.background = C.bg1}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <Img src={p.image_url} size={28} />
                  <div>
                    <div style={{ fontSize: 12, color: C.text1 }}>{p.name?.slice(0, 60)}</div>
                    <div style={{ fontSize: 10, color: C.text4 }}>{p.article} · {p.catalog_price} ₴</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function KitModal({ initial, allProducts, token, onSave, onClose }) {
  const isEdit = !!initial?.id;
  const [title, setTitle] = useState(initial?.title || "");
  const [startDate, setStartDate] = useState(initial?.start_date || TODAY);
  const [endDate, setEndDate] = useState(initial?.end_date || ONE_YEAR);
  const [mainId, setMainId] = useState(initial?.mainId || null);
  const [items, setItems] = useState(initial?.items || []);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const setMain = (id) => {
    setMainId(id);
    if (id) {
      const p = (allProducts || []).find(pp => pp.product_id === id);
      if (p && !title) setTitle(p.name?.slice(0, 60) || "");
    }
  };

  const addItem = () => setItems(prev => [...prev, { id: null, discount: 5, discountType: "relative" }]);
  const updateItem = (idx, patch) => setItems(prev => prev.map((it, i) => i === idx ? { ...it, ...patch } : it));
  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  const handleSave = async () => {
    if (!mainId) return setErr("Оберіть основний товар");
    if (!title.trim()) return setErr("Введіть назву комплекту");
    if (title.includes("+")) return setErr("Символ «+» заборонений у назві комплекту (правило Rozetka)");
    if (items.length === 0) return setErr("Додайте хоча б один дочірній товар");
    if (items.length > 3) return setErr("Максимум 3 дочірні товари");
    if (items.some(it => !it.id)) return setErr("Оберіть товар для кожного рядка");

    const secondItems = items.map(it => {
      const base = { item_id: it.id };
      if (it.discountType === "relative") return { ...base, relative_discount: Number(it.discount) };
      if (it.discountType === "fixed") return { ...base, fixed_discount: Number(it.discount) };
      return { ...base, fixed_amount: Number(it.discount) };
    });

    const payload = { title: title.trim(), item_id: mainId, start_date: startDate, secondItems };
    if (endDate) payload.end_date = endDate;

    setSaving(true); setErr(null);
    try {
      const url = isEdit ? `${BASE}/api/kits/${initial.id}` : `${BASE}/api/kits`;
      const method = isEdit ? "PUT" : "POST";
      const res = await fetch(url, {
        method, headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Помилка");
      onSave();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 200,
      display: "flex", alignItems: "center", justifyContent: "center" }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: C.bg0, borderRadius: 14, width: "min(560px,96vw)",
        maxHeight: "90vh", overflowY: "auto", padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: C.text1 }}>
            {isEdit ? "Редагувати комплект" : "Новий комплект"}
          </div>
          <span onClick={onClose} style={{ cursor: "pointer", color: C.text4, fontSize: 20 }}>×</span>
        </div>

        <div>
          <div style={{ fontSize: 11, color: C.text4, marginBottom: 4 }}>Назва комплекту</div>
          <input value={title} onChange={e => setTitle(e.target.value)}
            style={{ ...S.input, width: "100%", boxSizing: "border-box",
              borderColor: title.includes("+") ? C.pink : undefined }} placeholder="Назва..." />
          {title.includes("+") && (
            <div style={{ fontSize: 10, color: C.pink, marginTop: 3 }}>Символ «+» заборонений Rozetka — замініть на «та» або «і»</div>
          )}
        </div>

        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: C.text4, marginBottom: 4 }}>Дата старту</div>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              style={{ ...S.input, width: "100%", boxSizing: "border-box" }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: C.text4, marginBottom: 4 }}>Дата закінчення (необов'язково)</div>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
              style={{ ...S.input, width: "100%", boxSizing: "border-box" }} />
          </div>
        </div>

        <div>
          <div style={{ fontSize: 11, color: C.text4, marginBottom: 4 }}>Основний товар</div>
          <ProductPicker value={mainId} allProducts={allProducts} onChange={setMain} />
        </div>

        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: C.text4 }}>Дочірні товари</div>
            <span onClick={addItem} style={{ fontSize: 11, color: C.blue, cursor: "pointer", fontWeight: 600 }}>+ Додати товар</span>
          </div>
          {items.map((it, idx) => (
            <div key={idx} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 10,
              background: C.bg1, borderRadius: 8, padding: 10, minWidth: 0 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <ProductPicker value={it.id} allProducts={allProducts}
                  onChange={id => updateItem(idx, { id })} placeholder="Пошук дочірнього товару..." />
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
                <select value={it.discountType} onChange={e => updateItem(idx, { discountType: e.target.value })}
                  style={{ ...S.input, padding: "5px 8px", fontSize: 11 }}>
                  <option value="relative">% знижка</option>
                  <option value="fixed">₴ знижка</option>
                  <option value="amount">Фікс. ціна</option>
                </select>
                <input type="number" value={it.discount} min={0}
                  onChange={e => updateItem(idx, { discount: e.target.value })}
                  style={{ ...S.input, width: 64, textAlign: "center" }} />
                <span onClick={() => removeItem(idx)}
                  style={{ cursor: "pointer", color: C.text4, fontSize: 16, lineHeight: 1, padding: "0 2px" }}>×</span>
              </div>
            </div>
          ))}
          {items.length === 0 && (
            <div style={{ fontSize: 12, color: C.text4, textAlign: "center", padding: "12px 0" }}>
              Додайте хоча б один дочірній товар
            </div>
          )}
        </div>

        {err && <div style={{ fontSize: 12, color: C.pink, background: C.pink + "15", padding: "8px 12px", borderRadius: 8 }}>{err}</div>}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ ...S.btn(), background: C.bg1, color: C.text3 }}>Скасувати</button>
          <button onClick={handleSave} disabled={saving}
            style={{ ...S.btn(), background: C.blue, color: "#fff", opacity: saving ? 0.7 : 1 }}>
            {saving ? "Збереження..." : (isEdit ? "Зберегти" : "Створити комплект")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function KitsPage({ token, allProducts, isMobile }) {
  const [suggestions, setSuggestions] = useState([]);
  const [suggestLoading, setSuggestLoading] = useState(true);
  const [kits, setKits] = useState([]);
  const [kitsLoading, setKitsLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [statusChanging, setStatusChanging] = useState({});
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;

  // Bulk 1+1
  const [showBulk, setShowBulk] = useState(false);
  const [bulkDiscount, setBulkDiscount] = useState(50);
  const [bulkStartDate, setBulkStartDate] = useState(TODAY);
  const [bulkEndDate, setBulkEndDate] = useState(ONE_YEAR);
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkCreating, setBulkCreating] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null);

  // Bulk discount update
  const [showBulkDiscount, setShowBulkDiscount] = useState(false);
  const [bulkNewDiscount, setBulkNewDiscount] = useState(30);
  const [bulkDiscountConfirm, setBulkDiscountConfirm] = useState(false);
  const [bulkDiscountCreating, setBulkDiscountCreating] = useState(false);
  const [bulkDiscountProgress, setBulkDiscountProgress] = useState(null);

  // Other
  const [extendingDates, setExtendingDates] = useState(false);
  const [showAllWithoutKits, setShowAllWithoutKits] = useState(false);

  const KITS_CACHE_KEY = `kits_v1_${token?.slice(-20)}`;
  const KITS_TTL = 3 * 60 * 1000;
  const SUGG_CACHE_KEY = `kits_sugg_v1_${token?.slice(-20)}`;
  const SUGG_TTL = 10 * 60 * 1000;

  const load = useCallback(async (invalidate = false) => {
    if (!invalidate) {
      try {
        const cached = JSON.parse(localStorage.getItem(`kits_v1_${token?.slice(-20)}`) || "null");
        if (cached) {
          setKits(cached.data);
          setPage(1);
          setKitsLoading(false);
          if (Date.now() - cached.time < 3 * 60 * 1000) return;
        } else {
          setKitsLoading(true);
        }
      } catch {
        setKitsLoading(true);
      }
    } else {
      setKitsLoading(true);
    }
    try {
      const res = await fetch(`${BASE}/api/kits`, { headers: { Authorization: "Bearer " + token } });
      if (res.ok) {
        const d = await res.json();
        const kitsData = d.kits || [];
        setKits(kitsData);
        setPage(1);
        try { localStorage.setItem(`kits_v1_${token?.slice(-20)}`, JSON.stringify({ data: kitsData, time: Date.now() })); } catch {}
      }
    } finally {
      setKitsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
    try {
      const cached = JSON.parse(localStorage.getItem(`kits_sugg_v1_${token?.slice(-20)}`) || "null");
      if (cached) {
        setSuggestions(cached.data);
        setSuggestLoading(false);
        if (Date.now() - cached.time < 10 * 60 * 1000) return;
      }
    } catch {}
    fetch(`${BASE}/api/kits/suggestions`, { headers: { Authorization: "Bearer " + token } })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.suggestions) {
          setSuggestions(d.suggestions);
          try { localStorage.setItem(`kits_sugg_v1_${token?.slice(-20)}`, JSON.stringify({ data: d.suggestions, time: Date.now() })); } catch {}
        }
      })
      .finally(() => setSuggestLoading(false));
  }, [load, token]);

  const openEditKit = (kit) => {
    const mainId = kit.item?.item_id || kit.item?.id;
    setModal({
      initial: {
        id: kit.id,
        title: kit.title,
        start_date: kit.start_date,
        end_date: kit.end_date,
        mainId,
        items: (kit.second_items || []).map(si => {
          const id = si.item_id ?? si.item?.item_id ?? si.item?.id;
          return {
            id,
            discount: si.relative_discount ?? si.fixed_discount ?? si.fixed_amount ?? 0,
            discountType: si.relative_discount != null ? "relative"
              : si.fixed_discount != null ? "fixed" : "amount",
          };
        }),
      }
    });
  };

  const openCreate = (prefill = null) => {
    if (prefill) {
      const mainProd = (allProducts?.products || []).find(p => p.product_id === prefill.main?.rozetka_id);
      const secProd = prefill.secondary?.rozetka_id
        ? (allProducts?.products || []).find(p => p.product_id === prefill.secondary.rozetka_id)
        : null;
      const titleSuffix = prefill.secondary?.name
        ? ` та ${prefill.secondary.name?.slice(0, 25)}`
        : " — 1 та 1";
      setModal({
        initial: {
          title: `${prefill.main.name?.slice(0, 50)}${titleSuffix}`,
          mainId: mainProd ? mainProd.product_id : null,
          items: secProd
            ? [{ id: secProd.product_id, discount: prefill.secondary.suggested_discount || 5, discountType: "relative" }]
            : mainProd
            ? [{ id: mainProd.product_id, discount: 15, discountType: "relative" }]
            : [],
        }
      });
    } else {
      setModal({ initial: {} });
    }
  };

  const changeStatus = async (kitId, status) => {
    setStatusChanging(prev => ({ ...prev, [kitId]: true }));
    try {
      await fetch(`${BASE}/api/kits/${kitId}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
        body: JSON.stringify({ status }),
      });
      try { localStorage.removeItem(`kits_v1_${token?.slice(-20)}`); } catch {}
      await load(true);
    } finally {
      setStatusChanging(prev => ({ ...prev, [kitId]: false }));
    }
  };

  const archiveKit = async (kitId) => {
    if (!window.confirm("Архівувати комплект?")) return;
    await fetch(`${BASE}/api/kits/archive`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
      body: JSON.stringify({ includedIds: [kitId] }),
    });
    try { localStorage.removeItem(`kits_v1_${token?.slice(-20)}`); } catch {}
    await load(true);
  };

  // ── Bulk 1+1 ──────────────────────────────────────────────
  const createBulkOneForOne = async () => {
    const prods = allProducts?.products || [];
    if (!prods.length) return;
    setBulkConfirm(false);
    setBulkCreating(true);
    const total = prods.length;
    let done = 0;
    const errors = [];
    const createdIds = [];
    setBulkProgress({ done, total, errors, createdIds });
    const CONCURRENCY = 5;
    for (let i = 0; i < prods.length; i += CONCURRENCY) {
      const batch = prods.slice(i, i + CONCURRENCY);
      await Promise.all(batch.map(async p => {
        try {
          const res = await fetch(`${BASE}/api/kits`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
            body: JSON.stringify({
              title: `${(p.name || "").slice(0, 60)} — 1 та 1`,
              item_id: p.product_id,
              start_date: bulkStartDate,
              end_date: bulkEndDate,
              secondItems: [{ item_id: p.product_id, relative_discount: Number(bulkDiscount) }],
            }),
          });
          if (res.ok) {
            const d = await res.json();
            if (d.id) createdIds.push(d.id);
          } else {
            const d = await res.json();
            errors.push({ name: p.name?.slice(0, 30), err: d.detail || "помилка" });
          }
        } catch {
          errors.push({ name: p.name?.slice(0, 30), err: "мережева помилка" });
        }
        done++;
        setBulkProgress({ done, total, errors: [...errors], createdIds: [...createdIds] });
      }));
    }
    setBulkCreating(false);
    try { localStorage.removeItem(`kits_v1_${token?.slice(-20)}`); } catch {}
    await load(true);
  };

  const undoBulk = async () => {
    if (!bulkProgress?.createdIds?.length) return;
    await fetch(`${BASE}/api/kits/archive`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
      body: JSON.stringify({ includedIds: bulkProgress.createdIds }),
    });
    setBulkProgress(null);
    try { localStorage.removeItem(`kits_v1_${token?.slice(-20)}`); } catch {}
    await load(true);
  };

  // ── Bulk discount update ───────────────────────────────────
  const updateAllKitDiscounts = async () => {
    const activeKits = kits.filter(k => k.status === "active" && k.second_items?.length);
    setBulkDiscountConfirm(false);
    setBulkDiscountCreating(true);
    let done = 0;
    const errors = [];
    setBulkDiscountProgress({ done, total: activeKits.length, errors });
    const CONCURRENCY = 5;
    for (let i = 0; i < activeKits.length; i += CONCURRENCY) {
      const batch = activeKits.slice(i, i + CONCURRENCY);
      await Promise.all(batch.map(async kit => {
        try {
          const secondItems = (kit.second_items || []).map(si => ({
            item_id: si.item_id,
            relative_discount: Number(bulkNewDiscount),
          }));
          const res = await fetch(`${BASE}/api/kits/${kit.id}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
            body: JSON.stringify({ secondItems }),
          });
          if (!res.ok) {
            const d = await res.json();
            errors.push({ name: kit.title?.slice(0, 30), err: d.detail || "помилка" });
          }
        } catch {
          errors.push({ name: kit.title?.slice(0, 30), err: "мережева помилка" });
        }
        done++;
        setBulkDiscountProgress({ done, total: activeKits.length, errors: [...errors] });
      }));
    }
    setBulkDiscountCreating(false);
    try { localStorage.removeItem(`kits_v1_${token?.slice(-20)}`); } catch {}
    await load(true);
  };

  // ── Extend expiring kits ───────────────────────────────────
  const extendExpiringKits = async () => {
    setExtendingDates(true);
    const newDate = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
    await Promise.all(expiringSoon.map(kit =>
      fetch(`${BASE}/api/kits/${kit.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
        body: JSON.stringify({ end_date: newDate }),
      })
    ));
    setExtendingDates(false);
    try { localStorage.removeItem(`kits_v1_${token?.slice(-20)}`); } catch {}
    await load(true);
  };

  // ── Derived data ──────────────────────────────────────────
  const existingPairs = useMemo(() => {
    const s = new Set();
    kits.forEach(k => {
      const mainId = k.item?.item_id || k.item?.id;
      (k.second_items || []).forEach(si => {
        const siId = si.item_id ?? si.item?.item_id ?? si.item?.id;
        if (mainId && siId) {
          s.add(`${mainId}-${siId}`);
          s.add(`${siId}-${mainId}`);
        }
      });
    });
    return s;
  }, [kits]);

  const visibleSuggestions = useMemo(() =>
    suggestions.filter(s =>
      !existingPairs.has(`${s.main.rozetka_id}-${s.secondary.rozetka_id}`)
    ), [suggestions, existingPairs]);

  const existingMainIds = useMemo(() =>
    new Set(kits.map(k => k.item?.item_id || k.item?.id).filter(Boolean)), [kits]);

  const expiringSoon = useMemo(() =>
    kits.filter(k => k.end_date && k.end_date <= SOON_THRESHOLD && k.status !== "archived"),
  [kits]);

  const prodByRozetkaId = useMemo(() => {
    const m = {};
    (allProducts?.products || []).forEach(p => { m[p.product_id] = p; });
    return m;
  }, [allProducts]);

  const productsWithoutKits = useMemo(() =>
    (allProducts?.products || [])
      .filter(p => p.is_active !== false && p.status !== "inactive" && !existingMainIds.has(p.product_id))
      .sort((a, b) => (b.velocity || 0) - (a.velocity || 0) || (b.quantity || 0) - (a.quantity || 0))
      .slice(0, 40),
  [allProducts, existingMainIds]);

  const getKitMarginStatus = (kit) => {
    if (!kit.second_items?.length) return { status: null, pct: null };
    let worst = null;
    let totalMargin = 0, count = 0;
    for (const si of kit.second_items) {
      const itemId = si.item_id ?? si.item?.item_id ?? si.item?.id;
      const p = prodByRozetkaId[itemId];
      if (!p || !p.cost_price || !p.catalog_price) continue;
      const discount = si.relative_discount || 0;
      const basePrice = (p.catalog_promo_price > 0 && p.catalog_promo_price < p.catalog_price)
        ? p.catalog_promo_price : p.catalog_price;
      const effectivePrice = basePrice * (1 - discount / 100);
      const margin = (effectivePrice - p.cost_price) / effectivePrice * 100;
      totalMargin += margin;
      count++;
      if (margin < 0) worst = "loss";
      else if (margin < 10 && worst !== "loss") worst = "low";
    }
    if (count === 0) return { status: null, pct: null };
    return { status: worst, pct: Math.round(totalMargin / count) };
  };

  const allProds = allProducts?.products || [];
  const totalPages = Math.max(1, Math.ceil(kits.length / PAGE_SIZE));
  const pageKits = kits.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const BulkPanel = ({ color, title, children }) => (
    <div style={{ background: C.bg1, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, marginBottom: 20 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.text1, marginBottom: 12 }}>{title}</div>
      {children}
    </div>
  );

  const BulkProgress = ({ prog, color, onClose }) => prog && (
    <div style={{ marginTop: 12 }}>
      {prog.done < prog.total ? (
        <>
          <div style={{ fontSize: 12, color: C.text3, marginBottom: 4 }}>
            Виконується... {prog.done}/{prog.total}
          </div>
          <div style={{ background: C.bg2, borderRadius: 4, height: 6, overflow: "hidden" }}>
            <div style={{ background: color, height: "100%", transition: "width .2s",
              width: `${Math.round(prog.done / prog.total * 100)}%` }} />
          </div>
        </>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 12, color: C.green }}>
            ✓ Готово: {prog.total - (prog.errors?.length || 0)} успішно
            {prog.errors?.length > 0 && <span style={{ color: C.yellow }}>, {prog.errors.length} помилок</span>}
          </span>
          {prog.createdIds?.length > 0 && (
            <button onClick={undoBulk}
              style={{ ...S.btn(), fontSize: 11, padding: "3px 10px",
                background: C.pink + "22", color: C.pink, border: `1px solid ${C.pink}40` }}>
              Відмінити все ({prog.createdIds.length})
            </button>
          )}
          <span onClick={onClose} style={{ cursor: "pointer", color: C.text4, fontSize: 16 }}>×</span>
        </div>
      )}
    </div>
  );

  return (
    <div style={{ padding: isMobile ? "0 12px 24px" : "0 24px 24px", minWidth: 0, overflowX: "hidden" }}>
      {/* Header */}
      <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row",
        justifyContent: "space-between", alignItems: isMobile ? "stretch" : "center", gap: 12,
        paddingBottom: 20, borderBottom: `1px solid ${C.border}`, marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: C.text1 }}>🎁 Комплекти</div>
          <div style={{ fontSize: 12, color: C.text4, marginTop: 2 }}>Набори товарів зі знижками на Rozetka</div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
          <button onClick={() => { setShowBulk(v => !v); setBulkConfirm(false); }}
            style={{ ...S.btn(showBulk), fontWeight: 600 }}>
            1+1 для всіх {showBulk ? "▴" : "▾"}
          </button>
          <button onClick={() => { setShowBulkDiscount(v => !v); setBulkDiscountConfirm(false); }}
            style={{ ...S.btn(showBulkDiscount), fontWeight: 600 }}>
            Знижки {showBulkDiscount ? "▴" : "▾"}
          </button>
          <button onClick={() => openCreate()}
            style={{ ...S.btn(), background: C.blue, color: "#fff", fontWeight: 600 }}>
            + Новий комплект
          </button>
        </div>
      </div>

      {/* Bulk 1+1 panel */}
      {showBulk && (
        <BulkPanel color={C.purple} title="Масове створення 1+1">
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div>
              <div style={{ fontSize: 11, color: C.text4, marginBottom: 4 }}>Знижка на 2-й товар, %</div>
              <input type="number" value={bulkDiscount} min={1} max={100}
                onChange={e => setBulkDiscount(e.target.value)}
                style={{ ...S.input, width: 80, textAlign: "center" }} />
            </div>
            <div>
              <div style={{ fontSize: 11, color: C.text4, marginBottom: 4 }}>Початок</div>
              <input type="date" value={bulkStartDate} onChange={e => setBulkStartDate(e.target.value)}
                style={{ ...S.input }} />
            </div>
            <div>
              <div style={{ fontSize: 11, color: C.text4, marginBottom: 4 }}>Кінець</div>
              <input type="date" value={bulkEndDate} onChange={e => setBulkEndDate(e.target.value)}
                style={{ ...S.input }} />
            </div>
            <div style={{ marginLeft: "auto" }}>
              {!bulkConfirm ? (
                <button onClick={() => setBulkConfirm(true)} disabled={bulkCreating}
                  style={{ ...S.btn(), background: C.purple + "22", color: C.purple,
                    border: `1px solid ${C.purple}40`, fontWeight: 600 }}>
                  Створити для {(allProducts?.products || []).length} товарів →
                </button>
              ) : (
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span style={{ fontSize: 12, color: C.yellow }}>Ви впевнені?</span>
                  <button onClick={createBulkOneForOne}
                    style={{ ...S.btn(), background: C.purple, color: "#fff", fontWeight: 600 }}>
                    Так, запустити
                  </button>
                  <button onClick={() => setBulkConfirm(false)}
                    style={{ ...S.btn(), background: C.bg2, color: C.text3 }}>
                    Скасувати
                  </button>
                </div>
              )}
            </div>
          </div>
          <BulkProgress prog={bulkCreating || bulkProgress ? bulkProgress : null}
            color={C.purple} onClose={() => setBulkProgress(null)} />
        </BulkPanel>
      )}

      {/* Bulk discount update panel */}
      {showBulkDiscount && (
        <BulkPanel title="Масова зміна знижки у комплектах">
          <div style={{ fontSize: 12, color: C.text4, marginBottom: 10 }}>
            Змінить знижку у ВСІХ активних комплектах ({kits.filter(k => k.status === "active").length} шт.)
          </div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div>
              <div style={{ fontSize: 11, color: C.text4, marginBottom: 4 }}>Нова знижка, %</div>
              <input type="number" value={bulkNewDiscount} min={1} max={100}
                onChange={e => setBulkNewDiscount(e.target.value)}
                style={{ ...S.input, width: 80, textAlign: "center" }} />
            </div>
            <div style={{ marginLeft: "auto" }}>
              {!bulkDiscountConfirm ? (
                <button onClick={() => setBulkDiscountConfirm(true)} disabled={bulkDiscountCreating}
                  style={{ ...S.btn(), background: C.yellow + "22", color: C.yellow,
                    border: `1px solid ${C.yellow}40`, fontWeight: 600 }}>
                  Змінити знижку у всіх →
                </button>
              ) : (
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span style={{ fontSize: 12, color: C.yellow }}>Ви впевнені? Це змінить всі активні комплекти.</span>
                  <button onClick={updateAllKitDiscounts}
                    style={{ ...S.btn(), background: C.yellow, color: C.bg0, fontWeight: 600 }}>
                    Так, змінити
                  </button>
                  <button onClick={() => setBulkDiscountConfirm(false)}
                    style={{ ...S.btn(), background: C.bg2, color: C.text3 }}>
                    Скасувати
                  </button>
                </div>
              )}
            </div>
          </div>
          {(bulkDiscountCreating || bulkDiscountProgress) && (
            <div style={{ marginTop: 12 }}>
              {bulkDiscountCreating && bulkDiscountProgress ? (
                <>
                  <div style={{ fontSize: 12, color: C.text3, marginBottom: 4 }}>
                    Оновлюємо... {bulkDiscountProgress.done}/{bulkDiscountProgress.total}
                  </div>
                  <div style={{ background: C.bg2, borderRadius: 4, height: 6, overflow: "hidden" }}>
                    <div style={{ background: C.yellow, height: "100%", transition: "width .2s",
                      width: `${Math.round(bulkDiscountProgress.done / bulkDiscountProgress.total * 100)}%` }} />
                  </div>
                </>
              ) : bulkDiscountProgress && (
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: 12, color: C.green }}>
                    ✓ Оновлено {bulkDiscountProgress.total - bulkDiscountProgress.errors.length} з {bulkDiscountProgress.total}
                    {bulkDiscountProgress.errors.length > 0 && <span style={{ color: C.yellow }}>, {bulkDiscountProgress.errors.length} помилок</span>}
                  </span>
                  <span onClick={() => setBulkDiscountProgress(null)}
                    style={{ cursor: "pointer", color: C.text4, fontSize: 16 }}>×</span>
                </div>
              )}
            </div>
          )}
        </BulkPanel>
      )}

      {/* Expiring soon banner */}
      {!kitsLoading && expiringSoon.length > 0 && (
        <div style={{ background: C.yellow + "15", border: `1px solid ${C.yellow}40`,
          borderRadius: 12, padding: "12px 16px", marginBottom: 16,
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div>
            <span style={{ color: C.yellow, fontWeight: 700, fontSize: 13 }}>
              ⚠ {expiringSoon.length} комплектів закінчуються протягом 30 днів
            </span>
            <div style={{ fontSize: 11, color: C.text4, marginTop: 2 }}>
              {expiringSoon.slice(0, 3).map(k => k.title).join(", ")}{expiringSoon.length > 3 ? "..." : ""}
            </div>
          </div>
          <button onClick={extendExpiringKits} disabled={extendingDates}
            style={{ ...S.btn(), background: C.yellow + "22", color: C.yellow,
              border: `1px solid ${C.yellow}40`, fontWeight: 600, whiteSpace: "nowrap" }}>
            {extendingDates ? "Продовжуємо..." : "Продовжити всі на рік →"}
          </button>
        </div>
      )}

      {/* Products without kits */}
      {!kitsLoading && productsWithoutKits.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text1, marginBottom: 12 }}>
            🎯 Топ товари без комплекту
            <span style={{ color: C.text4, fontWeight: 400, marginLeft: 6 }}>({productsWithoutKits.length}+)</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(260px, 1fr))", gap: 8 }}>
            {productsWithoutKits.slice(0, showAllWithoutKits ? undefined : 8).map(p => (
              <div key={p.product_id} style={{ background: C.bg1, border: `1px solid ${C.border}`,
                borderLeft: `3px solid ${C.green}`, borderRadius: 10, padding: "10px 12px",
                display: "flex", gap: 10, alignItems: "center" }}>
                <Img src={p.image_url} size={32} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: C.text1, overflow: "hidden",
                    textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</div>
                  <div style={{ fontSize: 10, color: C.text4 }}>{p.catalog_price} ₴</div>
                </div>
                <button onClick={() => openCreate({ main: { rozetka_id: p.product_id, name: p.name }, secondary: null })}
                  style={{ ...S.btn(), fontSize: 10, padding: "3px 8px", whiteSpace: "nowrap",
                    background: C.green + "22", color: C.green, border: `1px solid ${C.green}40` }}>
                  + Кіт
                </button>
              </div>
            ))}
          </div>
          {productsWithoutKits.length > 8 && (
            <button onClick={() => setShowAllWithoutKits(v => !v)}
              style={{ ...S.btn(), marginTop: 8, fontSize: 11 }}>
              {showAllWithoutKits ? "Сховати" : `Показати всі ${productsWithoutKits.length} →`}
            </button>
          )}
        </div>
      )}

      {/* Suggestions */}
      {!suggestLoading && visibleSuggestions.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text1, marginBottom: 12 }}>
            💡 Рекомендовані комплекти на основі ваших продажів ({visibleSuggestions.length})
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(340px, 1fr))", gap: 10 }}>
            {visibleSuggestions.map((s, i) => (
              <div key={i} style={{ background: C.bg1, border: `1px solid ${C.border}`,
                borderLeft: `3px solid ${C.blue}`, borderRadius: 10, padding: "12px 14px",
                display: "flex", gap: 12, alignItems: "center" }}>
                <div style={{ display: "flex", gap: 6, alignItems: "center", flex: 1, minWidth: 0 }}>
                  <Img src={s.main.image_url} size={32} />
                  <div style={{ fontSize: 11, color: C.text2, lineHeight: 1.3, minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.main.name?.slice(0, 35)}</div>
                    <div style={{ color: C.text4 }}>{s.main.price} ₴</div>
                  </div>
                  <span style={{ color: C.text4, fontSize: 16 }}>+</span>
                  <Img src={s.secondary.image_url} size={32} />
                  <div style={{ fontSize: 11, color: C.text2, lineHeight: 1.3, minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.secondary.name?.slice(0, 35)}</div>
                    <div style={{ color: C.text4 }}>{s.secondary.price} ₴</div>
                  </div>
                </div>
                <div style={{ flexShrink: 0, textAlign: "right" }}>
                  <div style={{ fontSize: 10, color: C.text4, marginBottom: 4 }}>
                    ×{s.secondary.co_count} разів купували разом
                  </div>
                  <button onClick={() => openCreate(s)}
                    style={{ ...S.btn(), background: C.blue + "22", color: C.blue,
                      border: `1px solid ${C.blue}40`, fontSize: 11, padding: "4px 10px", fontWeight: 600 }}>
                    Створити →
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Kits list */}
      <div style={{ fontSize: 13, fontWeight: 700, color: C.text1, marginBottom: 12 }}>
        Мої комплекти {kits.length > 0 && <span style={{ color: C.text4, fontWeight: 400 }}>({kits.length})</span>}
      </div>

      {kitsLoading ? (
        <div style={{ color: C.text4, fontSize: 13, padding: "40px 0", textAlign: "center" }}>Завантаження...</div>
      ) : kits.length === 0 ? (
        <div style={{ color: C.text4, fontSize: 13, padding: "40px 0", textAlign: "center" }}>
          Комплектів поки немає.<br />
          <span style={{ color: C.blue, cursor: "pointer" }} onClick={() => openCreate()}>Створити перший →</span>
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, tableLayout: "fixed", minWidth: isMobile ? 0 : 800 }}>
          <thead>
            <tr style={{ background: C.bg1 }}>
              {[
                { label: "Товари в комплекті", width: isMobile ? 180 : 260, mobileHide: false },
                { label: "Назва комплекту",   width: isMobile ? 160 : 200, mobileHide: false },
                { label: "Статус",            width: 70,  mobileHide: true  },
                { label: "Дати",              width: 130, mobileHide: true  },
                { label: "Маржа",             width: 70,  mobileHide: false },
                { label: "Ціна набору",       width: 80,  mobileHide: true  },
                { label: "",                  width: undefined, mobileHide: false },
              ].filter(c => !(c.mobileHide && isMobile)).map(({ label, width }) => (
                <th key={label} style={{ padding: "8px 10px", textAlign: "left", fontSize: 10,
                  color: C.text4, fontWeight: 600, textTransform: "uppercase",
                  borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap",
                  ...(width ? { width } : {}) }}>{label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageKits.map(kit => {
              const item = kit.item || kit.item_main || {};
              const isChanging = statusChanging[kit.id];
              const { status: marginStatus, pct: marginPct } = getKitMarginStatus(kit);
              const mainItemId = item?.item_id || item?.id;
              const mainProd = prodByRozetkaId[mainItemId];
              const mainOnPromo = mainProd?.catalog_promo_price > 0 && mainProd?.catalog_promo_price < mainProd?.catalog_price;
              const rowBg = marginStatus === "loss" ? C.pink + "08" : marginStatus === "low" ? C.yellow + "08" : undefined;
              return (
                <tr key={kit.id}
                  onClick={e => { if (!e.target.closest("button") && !e.target.closest("select")) openEditKit(kit); }}
                  style={{ borderBottom: `1px solid ${C.border}`, background: rowBg, cursor: "pointer" }}>
                  <td style={{ ...S.td, overflow: "hidden" }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      {item.photo_preview && <Img src={item.photo_preview} size={26} />}
                      <div style={{ fontSize: 11, color: C.text2, minWidth: 0, flex: 1,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {item.name || "—"}
                      </div>
                    </div>
                    {(kit.second_items || []).map((si, idx) => {
                      const itemId = si.item_id ?? si.item?.item_id ?? si.item?.id;
                      const p = prodByRozetkaId[itemId];
                      const siName = si.item?.name || p?.name || (itemId ? `#${itemId}` : "?");
                      const siImg = si.item?.photo_preview || p?.image_url;
                      const disc = si.relative_discount != null ? `−${si.relative_discount}%`
                        : si.fixed_discount != null ? `−${si.fixed_discount}₴`
                        : si.fixed_amount != null ? `=${si.fixed_amount}₴` : "";
                      return (
                        <div key={idx} style={{ display: "flex", gap: 6, alignItems: "center",
                          marginTop: 5, paddingTop: 5, borderTop: `1px solid ${C.border}` }}>
                          {siImg && <Img src={siImg} size={22} />}
                          <div style={{ fontSize: 10, color: C.text3, flex: 1, minWidth: 0,
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {siName}
                          </div>
                          {disc && (
                            <span style={{ fontSize: 10, color: C.green, fontWeight: 700, flexShrink: 0 }}>
                              {disc}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </td>
                  <td style={{ ...S.td, maxWidth: 180 }}>
                    <div style={{ fontWeight: 600, color: C.text1, overflow: "hidden",
                      textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{kit.title}</div>
                    <div style={{ fontSize: 10, color: C.text4 }}>#{kit.id}</div>
                  </td>
                  {!isMobile && <td style={S.td}><StatusBadge status={kit.status} /></td>}
                  {!isMobile && <td style={{ ...S.td, fontSize: 11, color: C.text3 }}>
                    {kit.start_date}
                    {kit.end_date ? <> — {kit.end_date}</> : <span style={{ color: C.text4 }}> — ∞</span>}
                  </td>}
                  <td style={S.td}>
                    {marginPct !== null && (
                      <span style={{ fontSize: 12, fontWeight: 700,
                        color: marginStatus === "loss" ? C.pink : marginStatus === "low" ? C.yellow : C.green }}>
                        {marginPct}%
                      </span>
                    )}
                    {marginPct === null && <span style={{ color: C.text4, fontSize: 11 }}>—</span>}
                    {mainOnPromo && (
                      <div style={{ fontSize: 10, color: C.yellow, marginTop: 2 }}>⚠ Осн. на промо</div>
                    )}
                    {marginStatus === "loss" && (
                      <div style={{ fontSize: 10, fontWeight: 700, color: C.pink,
                        background: C.pink + "22", padding: "2px 6px", borderRadius: 4, marginTop: 2, display: "inline-block" }}>Збиток</div>
                    )}
                    {marginStatus === "low" && (
                      <div style={{ fontSize: 10, fontWeight: 700, color: C.yellow,
                        background: C.yellow + "22", padding: "2px 6px", borderRadius: 4, marginTop: 2, display: "inline-block" }}>Низька</div>
                    )}
                  </td>
                  {!isMobile && <td style={{ ...S.td, fontWeight: 600, color: C.text1 }}>
                    {kit.total_price ? `${kit.total_price} ₴` : "—"}
                  </td>}
                  <td style={{ ...S.td }}>
                    <div style={{ display: "flex", gap: 6 }}>
                      {kit.status === "active" ? (
                        <button onClick={() => changeStatus(kit.id, "locked")} disabled={isChanging}
                          style={{ ...S.btn(), fontSize: 10, padding: "3px 8px",
                            background: C.yellow + "22", color: C.yellow, border: `1px solid ${C.yellow}40` }}>
                          Зупинити
                        </button>
                      ) : kit.status === "locked" ? (
                        <button onClick={() => changeStatus(kit.id, "active")} disabled={isChanging}
                          style={{ ...S.btn(), fontSize: 10, padding: "3px 8px",
                            background: C.green + "22", color: C.green, border: `1px solid ${C.green}40` }}>
                          Активувати
                        </button>
                      ) : null}
                      <button onClick={() => archiveKit(kit.id)}
                        style={{ ...S.btn(), fontSize: 10, padding: "3px 8px",
                          background: C.bg1, color: C.text4, border: `1px solid ${C.border}` }}>
                        Архів
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      )}

      {/* Pagination */}
      {!kitsLoading && totalPages > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center",
          gap: 8, marginTop: 16 }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            style={{ ...S.btn(), padding: "4px 12px", opacity: page === 1 ? 0.4 : 1 }}>‹</button>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
            .reduce((acc, p, idx, arr) => {
              if (idx > 0 && p - arr[idx - 1] > 1) acc.push("…");
              acc.push(p);
              return acc;
            }, [])
            .map((p, i) => p === "…"
              ? <span key={`e${i}`} style={{ color: C.text4, padding: "0 4px" }}>…</span>
              : <button key={p} onClick={() => setPage(p)}
                  style={{ ...S.btn(p === page), padding: "4px 10px",
                    minWidth: 32, textAlign: "center" }}>{p}</button>
            )}
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
            style={{ ...S.btn(), padding: "4px 12px", opacity: page === totalPages ? 0.4 : 1 }}>›</button>
          <span style={{ fontSize: 11, color: C.text4, marginLeft: 4 }}>
            {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, kits.length)} з {kits.length}
          </span>
        </div>
      )}

      {modal && (
        <KitModal
          initial={modal.initial}
          allProducts={allProds}
          token={token}
          onSave={() => { setModal(null); try { localStorage.removeItem(`kits_v1_${token?.slice(-20)}`); } catch {} load(true); }}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
